import {useCallback, useMemo} from 'react';

import {useMediaQuery} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {AppButton, AppButtonVariant} from '@/app/shared-components/AppButton';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import logError from '@/utils/errors/logError';
import {buildSplitGroupInviteLink} from '@/utils/goLinks/buildSplitGroupInviteLink';
import {buildSplitInviteLink} from '@/utils/goLinks/buildSplitInviteLink';

import {useGroupsTrackers} from '../../groups/trackers';

interface SplitShareLinkCardProps {
  splitId: string;
  groupId?: string;
  groupName?: string;
  splitUserId?: string;
  splitUserName?: string;
  inviterName?: string;
}

const fileName = 'SplitShareLinkCard.tsx';

export const SplitShareLinkCard = ({
  splitId,
  groupId,
  groupName,
  inviterName,
}: SplitShareLinkCardProps) => {
  const {t} = useTranslation();
  const isMobile = useMediaQuery('(max-width: 959px)');

  const {
    trackSelectMoCopySplitLink,
    trackSelectMoShareSplitLink,
    trackShareMoSplitLinkSuccess,
  } = useGroupsTrackers(groupId);

  const shareUrl = useMemo(() => {
    if (groupId) {
      return buildSplitGroupInviteLink(groupId, inviterName);
    }

    return buildSplitInviteLink(splitId);
  }, [splitId, groupId, inviterName]);

  const displayShareUrl = useMemo(() => {
    const MAX_DISPLAY_LEN = isMobile ? 48 : 64;
    if (shareUrl.length <= MAX_DISPLAY_LEN) return shareUrl;

    return `${shareUrl.slice(0, MAX_DISPLAY_LEN - 3)}...`;
  }, [shareUrl, isMobile]);

  const handleCopy = useCallback(async () => {
    trackSelectMoCopySplitLink();
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSnackbar({
        summary: t('split_bills_page.success.share_link_copy'),
        message: t('split_bills_page.success.share_link_copied_message'),
        variant: SnackbarType.SUCCESS,
      });
    } catch (error) {
      logError(error, {
        fileName,
        functionName: 'handleCopy',
        customTitle: 'Mobot - Error copying split link',
        data: {shareUrl},
      });
    }
  }, [shareUrl, t, trackSelectMoCopySplitLink]);

  const handleShare = useCallback(async () => {
    trackSelectMoShareSplitLink();
    try {
      if (navigator.share) {
        const isGroupSplit = Boolean(groupId);

        const shareTitle = isGroupSplit
          ? t('split_bills_page.success.join_group_split_title')
          : t('split_bills_page.success.share_link_for_splits_title');

        const shareText = isGroupSplit
          ? t('split_bills_page.success.join_group_split_text', {
              groupName: groupName ?? groupId,
              goLink: shareUrl,
              interpolation: {escapeValue: false},
            })
          : t('split_bills_page.success.share_link_for_splits_text', {
              goLink: shareUrl,
            });

        await navigator.share({
          title: shareTitle,
          text: shareText,
        });
        trackShareMoSplitLinkSuccess();
        return;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        logError(error, {
          fileName,
          functionName: 'handleShare',
          customTitle: 'Mobot - Error sharing split link',
          data: {shareUrl},
        });
      }
    }

    handleCopy();
  }, [
    trackSelectMoShareSplitLink,
    handleCopy,
    groupId,
    t,
    groupName,
    shareUrl,
    trackShareMoSplitLinkSuccess,
  ]);

  return (
    <div className="split-share-link-card">
      <div className="split-share-link-card-header">
        <p className="split-share-link-card-title">
          {t('split_bills_page.success.share_link_title')}
        </p>
        <p className="split-share-link-card-subtitle">
          {t('split_bills_page.success.share_link_subtitle')}
        </p>
      </div>

      <div
        className="split-share-link-card-url"
        title={shareUrl}>
        {displayShareUrl}
      </div>

      <div className="split-share-link-card-actions">
        <div className="split-share-link-card-actions-copy">
          <AppButton
            id="split-share-link-copy"
            label={t('split_bills_page.success.share_link_copy')}
            variant={AppButtonVariant.onQuinary}
            onClick={handleCopy}
            iconLeft={
              <img
                src="/assets/icons/content_copy.svg"
                alt={t('split_bills_page.success.share_link_copy')}
                style={{width: 16, height: 16}}
              />
            }
          />
        </div>
        <div className="split-share-link-card-actions-share">
          <AppButton
            id="split-share-link-share"
            label={t('split_bills_page.success.share_link_button')}
            variant={AppButtonVariant.onPrimary}
            onClick={handleShare}
            iconLeft={
              <img
                src="/assets/icons/share_link.svg"
                alt={t('split_bills_page.success.share_link_button')}
                style={{width: 20, height: 20}}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};
